import { getFirebaseToken } from '../firebase';

/**
 * FarmRoadAutomationService
 *
 * Service pour automatiser le rafraîchissement des données et l'analyse IA
 * Fonctionne avec un scheduler backend (cron jobs)
 *
 * Fonctionnalités :
 * - Configuration des tâches planifiées (7h du matin)
 * - Exécution manuelle des tâches
 * - Monitoring de l'état des automatisations
 * - Historique des exécutions
 */
class FarmRoadAutomationService {
  constructor() {
    this.baseUrl = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8080';
    this.apiPrefix = '/api/farmroad/automation';
  }

  /**
   * Get headers with Firebase authentication token
   */
  async _headers() {
    const token = await getFirebaseToken();
    return {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json'
    };
  }

  // =========================================================================
  // CONFIGURATION DES AUTOMATISATIONS
  // =========================================================================

  /**
   * Récupérer la configuration d'automatisation pour une ferme
   * @param {string} farmId - ID de la ferme
   * @returns {Promise<Object>} Configuration d'automatisation
   */
  async getAutomationConfig(farmId) {
    try {
      const response = await fetch(`${this.baseUrl}${this.apiPrefix}/config/${farmId}`, {
        method: 'GET',
        headers: await this._headers()
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Failed to fetch automation config');
      }

      const result = await response.json();
      console.log('[AutomationService] Config fetched for farm:', farmId);
      return result.data;
    } catch (error) {
      console.error('[AutomationService] Error fetching config:', error);
      throw error;
    }
  }

  /**
   * Créer ou mettre à jour la configuration d'automatisation
   * @param {Object} config - Configuration
   * @returns {Promise<Object>} Configuration sauvegardée
   */
  async saveAutomationConfig(config) {
    try {
      const response = await fetch(`${this.baseUrl}${this.apiPrefix}/config`, {
        method: 'POST',
        headers: await this._headers(),
        body: JSON.stringify(config)
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Failed to save automation config');
      }

      const result = await response.json();
      console.log('[AutomationService] Config saved:', result.data);
      return result.data;
    } catch (error) {
      console.error('[AutomationService] Error saving config:', error);
      throw error;
    }
  }

  /**
   * Activer/Désactiver l'automatisation pour une ferme
   * @param {string} farmId - ID de la ferme
   * @param {boolean} enabled - Activer ou désactiver
   * @returns {Promise<Object>} Résultat
   */
  async toggleAutomation(farmId, enabled) {
    try {
      const response = await fetch(`${this.baseUrl}${this.apiPrefix}/toggle/${farmId}`, {
        method: 'POST',
        headers: await this._headers(),
        body: JSON.stringify({ enabled })
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Failed to toggle automation');
      }

      const result = await response.json();
      console.log('[AutomationService] Automation toggled:', farmId, enabled);
      return result;
    } catch (error) {
      console.error('[AutomationService] Error toggling automation:', error);
      throw error;
    }
  }

  // =========================================================================
  // EXÉCUTION MANUELLE
  // =========================================================================

  /**
   * Déclencher manuellement le rafraîchissement des données
   * @param {string} farmId - ID de la ferme
   * @param {Object} options - Options de rafraîchissement
   * @returns {Promise<Object>} Résultat de l'exécution
   */
  async triggerDataRefresh(farmId, options = {}) {
    try {
      const response = await fetch(`${this.baseUrl}${this.apiPrefix}/trigger/refresh/${farmId}`, {
        method: 'POST',
        headers: await this._headers(),
        body: JSON.stringify(options)
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Failed to trigger data refresh');
      }

      const result = await response.json();
      console.log('[AutomationService] Data refresh triggered:', farmId);
      return result;
    } catch (error) {
      console.error('[AutomationService] Error triggering refresh:', error);
      throw error;
    }
  }

  /**
   * Déclencher manuellement l'analyse IA
   * @param {string} farmId - ID de la ferme
   * @param {Object} options - Options d'analyse
   * @returns {Promise<Object>} Résultat de l'analyse
   */
  async triggerAIAnalysis(farmId, options = {}) {
    try {
      const response = await fetch(`${this.baseUrl}${this.apiPrefix}/trigger/analysis/${farmId}`, {
        method: 'POST',
        headers: await this._headers(),
        body: JSON.stringify(options)
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Failed to trigger AI analysis');
      }

      const result = await response.json();
      console.log('[AutomationService] AI analysis triggered:', farmId);
      return result;
    } catch (error) {
      console.error('[AutomationService] Error triggering analysis:', error);
      throw error;
    }
  }

  /**
   * Déclencher manuellement la routine complète (refresh + analysis)
   * @param {string} farmId - ID de la ferme
   * @param {Object} options - Options { targetDate: 'YYYY-MM-DD' }
   * @returns {Promise<Object>} Résultat complet
   */
  async triggerFullRoutine(farmId, options = {}) {
    try {
      const response = await fetch(`${this.baseUrl}${this.apiPrefix}/trigger/full/${farmId}`, {
        method: 'POST',
        headers: await this._headers(),
        body: JSON.stringify(options)
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Failed to trigger full routine');
      }

      const result = await response.json();
      console.log('[AutomationService] Full routine triggered:', farmId, options.targetDate || 'today');
      return result;
    } catch (error) {
      console.error('[AutomationService] Error triggering full routine:', error);
      throw error;
    }
  }

  /**
   * Déclencher manuellement le refresh météo complet (prévisions + agrégation)
   * @param {string} farmId - ID de la ferme
   * @param {Object} options - Options { targetDate }
   * @returns {Promise<Object>} Résultat du refresh météo
   */
  async triggerWeatherRefresh(farmId, options = {}) {
    try {
      const response = await fetch(`${this.baseUrl}${this.apiPrefix}/trigger/weather/${farmId}`, {
        method: 'POST',
        headers: await this._headers(),
        body: JSON.stringify(options)
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Failed to trigger weather refresh');
      }

      const result = await response.json();
      console.log('[AutomationService] Weather refresh triggered:', farmId);
      return result;
    } catch (error) {
      console.error('[AutomationService] Error triggering weather refresh:', error);
      throw error;
    }
  }

  /**
   * Déclencher uniquement la récupération des prévisions météo (sans agrégation)
   * @param {string} farmId - ID de la ferme
   * @returns {Promise<Object>} Résultat de la récupération des prévisions
   */
  async triggerWeatherForecast(farmId) {
    try {
      const response = await fetch(`${this.baseUrl}${this.apiPrefix}/trigger/weather-forecast/${farmId}`, {
        method: 'POST',
        headers: await this._headers()
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Failed to trigger weather forecast');
      }

      const result = await response.json();
      console.log('[AutomationService] Weather forecast triggered:', farmId);
      return result;
    } catch (error) {
      console.error('[AutomationService] Error triggering weather forecast:', error);
      throw error;
    }
  }

  /**
   * Déclencher uniquement l'agrégation des données historiques (sans prévisions)
   * @param {string} farmId - ID de la ferme
   * @param {Object} options - Options { targetDate }
   * @returns {Promise<Object>} Résultat de l'agrégation
   */
  async triggerWeatherAggregation(farmId, options = {}) {
    try {
      const response = await fetch(`${this.baseUrl}${this.apiPrefix}/trigger/weather-aggregation/${farmId}`, {
        method: 'POST',
        headers: await this._headers(),
        body: JSON.stringify(options)
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Failed to trigger weather aggregation');
      }

      const result = await response.json();
      console.log('[AutomationService] Weather aggregation triggered:', farmId);
      return result;
    } catch (error) {
      console.error('[AutomationService] Error triggering weather aggregation:', error);
      throw error;
    }
  }

  // =========================================================================
  // MONITORING ET HISTORIQUE
  // =========================================================================

  /**
   * Récupérer le statut actuel de l'automatisation
   * @param {string} farmId - ID de la ferme
   * @returns {Promise<Object>} Statut actuel
   */
  async getStatus(farmId) {
    try {
      const response = await fetch(`${this.baseUrl}${this.apiPrefix}/status/${farmId}`, {
        method: 'GET',
        headers: await this._headers()
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Failed to fetch status');
      }

      const result = await response.json();
      return result.data;
    } catch (error) {
      console.error('[AutomationService] Error fetching status:', error);
      throw error;
    }
  }

  /**
   * Récupérer l'historique des exécutions
   * @param {string} farmId - ID de la ferme
   * @param {Object} filters - Filtres optionnels
   * @returns {Promise<Object>} Historique paginé
   */
  async getExecutionHistory(farmId, filters = {}) {
    try {
      const params = new URLSearchParams({
        ...(filters.startDate && { startDate: filters.startDate }),
        ...(filters.endDate && { endDate: filters.endDate }),
        ...(filters.status && { status: filters.status }),
        ...(filters.type && { type: filters.type }),
        limit: filters.limit || 50,
        offset: filters.offset || 0
      });

      const response = await fetch(`${this.baseUrl}${this.apiPrefix}/history/${farmId}?${params}`, {
        method: 'GET',
        headers: await this._headers()
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Failed to fetch execution history');
      }

      const result = await response.json();
      console.log('[AutomationService] Execution history fetched:', result.pagination);
      return result;
    } catch (error) {
      console.error('[AutomationService] Error fetching history:', error);
      throw error;
    }
  }

  /**
   * Récupérer les statistiques d'automatisation
   * @param {string} farmId - ID de la ferme
   * @param {Object} filters - Filtres optionnels (période)
   * @returns {Promise<Object>} Statistiques
   */
  async getStats(farmId, filters = {}) {
    try {
      const params = new URLSearchParams({
        ...(filters.startDate && { startDate: filters.startDate }),
        ...(filters.endDate && { endDate: filters.endDate })
      });

      const response = await fetch(`${this.baseUrl}${this.apiPrefix}/stats/${farmId}?${params}`, {
        method: 'GET',
        headers: await this._headers()
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Failed to fetch stats');
      }

      const result = await response.json();
      console.log('[AutomationService] Stats fetched for farm:', farmId);
      return result.data;
    } catch (error) {
      console.error('[AutomationService] Error fetching stats:', error);
      throw error;
    }
  }

  /**
   * Récupérer le rapport de supervision (disponibilité des données)
   * @param {string} farmId - ID de la ferme
   * @param {Date} date - Date cible (par défaut aujourd'hui)
   * @returns {Promise<Object>} Rapport de supervision
   */
  async getMonitoringReport(farmId, date = new Date()) {
    try {
      const params = new URLSearchParams({
        date: date.toISOString()
      });

      const response = await fetch(`${this.baseUrl}${this.apiPrefix}/monitoring/${farmId}?${params}`, {
        method: 'GET',
        headers: await this._headers()
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Failed to fetch monitoring report');
      }

      const result = await response.json();
      console.log('[AutomationService] Monitoring report fetched:', farmId);
      return result.data;
    } catch (error) {
      console.error('[AutomationService] Error fetching monitoring report:', error);
      throw error;
    }
  }

  // =========================================================================
  // ALERTES ET NOTIFICATIONS
  // =========================================================================

  /**
   * Récupérer les alertes actives
   * @param {string} farmId - ID de la ferme
   * @returns {Promise<Array>} Liste des alertes
   */
  async getActiveAlerts(farmId) {
    try {
      const response = await fetch(`${this.baseUrl}${this.apiPrefix}/alerts/${farmId}`, {
        method: 'GET',
        headers: await this._headers()
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Failed to fetch alerts');
      }

      const result = await response.json();
      return result.data || [];
    } catch (error) {
      console.error('[AutomationService] Error fetching alerts:', error);
      throw error;
    }
  }

  /**
   * Marquer une alerte comme lue/résolue
   * @param {string} alertId - ID de l'alerte
   * @returns {Promise<Object>} Résultat
   */
  async acknowledgeAlert(alertId) {
    try {
      const response = await fetch(`${this.baseUrl}${this.apiPrefix}/alerts/${alertId}/acknowledge`, {
        method: 'POST',
        headers: await this._headers()
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Failed to acknowledge alert');
      }

      const result = await response.json();
      console.log('[AutomationService] Alert acknowledged:', alertId);
      return result;
    } catch (error) {
      console.error('[AutomationService] Error acknowledging alert:', error);
      throw error;
    }
  }
}

export default new FarmRoadAutomationService();
